import { DateTime } from 'luxon';
import type { Component } from 'vue';
import { Repeat, RepeatOff } from '@lucide/vue';
import type { ExpiringProgramSummary, ProgramsExpiringConfig } from '@/types/v2/DashboardWidget';
import type { TableSort, TableSortOption } from '@/utilities/TableSort';

export const PROGRAMS_EXPIRING_DAY_OPTIONS = [30, 60, 90, 120] as const;

export const DEFAULT_PROGRAMS_EXPIRING_CONFIG: ProgramsExpiringConfig = { days: 30, servicingRole: true, favorited: true };

/** Configs saved before the source filter existed hold only `days`; the API rejects a request with neither source. */
export function resolveProgramsExpiringConfig(config: Partial<ProgramsExpiringConfig> | null | undefined): ProgramsExpiringConfig {
  const days = config?.days ?? DEFAULT_PROGRAMS_EXPIRING_CONFIG.days;
  const servicingRole = config?.servicingRole ?? true;
  const favorited = config?.favorited ?? true;
  if (!servicingRole && !favorited) return { days, servicingRole: true, favorited: true };
  return { days, servicingRole, favorited };
}

type SortField = keyof Pick<
  ExpiringProgramSummary,
  'expiresAt' | 'accountName' | 'programName' | 'maxLimitInclAttachment' | 'retention' | 'totalPremiumSum'
>;

const SOONEST_EXPIRATION: TableSort = { key: 'expiresAt', direction: 'ascending' };
const CLIENT_A_TO_Z: TableSort = { key: 'accountName', direction: 'ascending' };

export const PROGRAMS_EXPIRING_SORT_OPTIONS: TableSortOption[] = [
  {
    value: 'expiration',
    label: 'Expiration',
    description: 'Soonest expiration first',
    reversedDescription: 'Latest expiration first',
    sorts: [SOONEST_EXPIRATION, CLIENT_A_TO_Z],
  },
  {
    value: 'client',
    label: 'Client',
    description: 'Client A–Z',
    reversedDescription: 'Client Z–A',
    sorts: [CLIENT_A_TO_Z, SOONEST_EXPIRATION],
  },
  {
    value: 'program',
    label: 'Program',
    description: 'Program A–Z',
    reversedDescription: 'Program Z–A',
    sorts: [{ key: 'programName', direction: 'ascending' }, CLIENT_A_TO_Z],
  },
  {
    value: 'limit',
    label: 'Limit',
    description: 'Highest limit first',
    reversedDescription: 'Lowest limit first',
    sorts: [{ key: 'maxLimitInclAttachment', direction: 'descending' }, SOONEST_EXPIRATION],
  },
  {
    value: 'retention',
    label: 'Retention',
    description: 'Highest retention first',
    reversedDescription: 'Lowest retention first',
    sorts: [{ key: 'retention', direction: 'descending' }, SOONEST_EXPIRATION],
  },
  {
    value: 'premium',
    label: 'Premium',
    description: 'Highest premium first',
    reversedDescription: 'Lowest premium first',
    sorts: [{ key: 'totalPremiumSum', direction: 'descending' }, SOONEST_EXPIRATION],
  },
];

export const DEFAULT_PROGRAMS_EXPIRING_SORT = PROGRAMS_EXPIRING_SORT_OPTIONS[0].value;

export type Recurrence = 'recurring' | 'nonRecurring';
export type RecurrenceFilter = 'all' | Recurrence;

const RECURRENCES: Recurrence[] = ['recurring', 'nonRecurring'];

export const RECURRENCE_LABELS: Record<Recurrence, string> = {
  recurring: 'Recurring',
  nonRecurring: 'Non-Recurring',
};

export const RECURRENCE_DESCRIPTIONS: Record<Recurrence, string> = {
  recurring: 'Expected to renew each term',
  nonRecurring: 'One-time / non-renewing policy',
};

export const RECURRENCE_STYLES: Record<Recurrence, { icon: Component; tagClass: string }> = {
  recurring: { icon: Repeat, tagClass: 'bg-[#E1F5EE] text-[#0F6E56]' },
  nonRecurring: { icon: RepeatOff, tagClass: 'bg-[#FAEEDA] text-[#854F0B]' },
};

export const RECURRENCE_FILTER_OPTIONS: { value: RecurrenceFilter; label: string; icon?: Component }[] = [
  { value: 'all', label: 'All' },
  ...RECURRENCES.map((r) => ({ value: r, label: RECURRENCE_LABELS[r], icon: RECURRENCE_STYLES[r].icon })),
];

/** An API without the recurrence fields leaves both unset; unknown recurrence counts as recurring. */
export function recurrencesOf(program: ExpiringProgramSummary): Recurrence[] {
  const recurrences: Recurrence[] = [];
  if (program.hasRecurring) recurrences.push('recurring');
  if (program.hasNonRecurring) recurrences.push('nonRecurring');
  return recurrences.length > 0 ? recurrences : ['recurring'];
}

export function matchesRecurrenceFilter(program: ExpiringProgramSummary, filter: RecurrenceFilter): boolean {
  return filter === 'all' || recurrencesOf(program).includes(filter);
}

export function countRecurrences(programs: ExpiringProgramSummary[]): Record<Recurrence, number> {
  const counts: Record<Recurrence, number> = { recurring: 0, nonRecurring: 0 };
  for (const program of programs) {
    for (const recurrence of recurrencesOf(program)) counts[recurrence]++;
  }
  return counts;
}

function sortableValue(program: ExpiringProgramSummary, field: SortField): string | number | null {
  const value = program[field];
  if (value == null || value === '') return null;
  if (field === 'expiresAt') return DateTime.fromISO(String(value), { zone: 'utc' }).toMillis();
  return value;
}

function compareValues(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

/** Missing values sit below every real value in both directions, so reversing never floods the top with dashes. */
export function sortExpiringPrograms(programs: ExpiringProgramSummary[], sorts: TableSort[]): ExpiringProgramSummary[] {
  return programs.slice().sort((a, b) => {
    for (const { key, direction } of sorts) {
      const aValue = sortableValue(a, key as SortField);
      const bValue = sortableValue(b, key as SortField);
      if (aValue === null || bValue === null) {
        if (aValue === bValue) continue;
        return aValue === null ? 1 : -1;
      }
      const result = compareValues(aValue, bValue);
      if (result !== 0) return direction === 'descending' ? -result : result;
    }
    return 0;
  });
}

export type ProgramsExpiringLayout = 'full' | 'compact' | 'stacked';

const FULL_LAYOUT_MIN_WIDTH = 640;
const COMPACT_LAYOUT_MIN_WIDTH = 420;

export function layoutForWidth(width: number): ProgramsExpiringLayout {
  if (width >= FULL_LAYOUT_MIN_WIDTH) return 'full';
  if (width >= COMPACT_LAYOUT_MIN_WIDTH) return 'compact';
  return 'stacked';
}

export function programsExpiringTitle(days: number): string {
  return `Programs Expiring in ${days} Days`;
}

export function programsExpiringFileName(days: number, date: DateTime, filter: RecurrenceFilter = 'all'): string {
  const filterPart = filter === 'all' ? '' : `${RECURRENCE_LABELS[filter].replace('-', '_')}_`;
  return `Programs_Expiring_${days}_Days_${filterPart}${date.toFormat('yyyy_MM_dd')}`;
}

function csvCell(value: string | number | null | undefined): string {
  if (value == null || value === '') return '';
  return `"${String(value).replace(/"/g, '""')}"`;
}

export function programsExpiringCsv(programs: ExpiringProgramSummary[]): string {
  const header = 'Client,Program,Recurrence,Limit,Retention,Premium,Expiration';
  const rows = programs.map((p) =>
    [
      p.accountName,
      p.programName,
      recurrencesOf(p).map((r) => RECURRENCE_LABELS[r]).join('; '),
      p.maxLimitInclAttachment,
      p.retention,
      p.totalPremiumSum,
      p.expiresAt ? DateTime.fromISO(p.expiresAt, { zone: 'utc' }).toISODate() : null,
    ]
      .map(csvCell)
      .join(','),
  );
  return [header, ...rows].join('\n');
}
